import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import { BACKEND_URL } from '../config/canonical';
import {
  Building2, Plus, Search, RefreshCw, Users, FolderOpen, Mail, Phone, MapPin, Trash2, Edit, Copy, Key
} from 'lucide-react';

const MainHubCompanies = () => {
  const token = localStorage.getItem('nexus_main_hub_token');
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [form, setForm] = useState({
    name: '', type: 'investigation_agency', contact_email: '', contact_phone: '',
    address: '', city: '', state: '', zip_code: '', license_number: '', website: '', description: ''
  });

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/main-hub/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setCompanies(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

  const handleCreate = async () => {
    if (!form.name || !form.contact_email) {
      toast({ title: 'Error', description: 'Name and email are required', variant: 'destructive' });
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/companies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        toast({ title: 'Success', description: 'Company created' });
        setShowCreate(false);
        setForm({ name: '', type: 'investigation_agency', contact_email: '', contact_phone: '', address: '', city: '', state: '', zip_code: '', license_number: '', website: '', description: '' });
        fetchCompanies();
      } else {
        const err = await res.json();
        throw new Error(err.detail || 'Failed');
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const handleEdit = async () => {
    if (!form.name || !form.contact_email) {
      toast({ title: 'Error', description: 'Name and email are required', variant: 'destructive' });
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/companies/${selectedCompany.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        toast({ title: 'Success', description: 'Company updated' });
        setShowEdit(false);
        setSelectedCompany(null);
        setForm({ name: '', type: 'investigation_agency', contact_email: '', contact_phone: '', address: '', city: '', state: '', zip_code: '', license_number: '', website: '', description: '' });
        fetchCompanies();
      } else {
        const err = await res.json();
        throw new Error(err.detail || 'Failed');
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const handleDelete = async (companyId, companyName) => {
    if (!window.confirm(`Delete company "${companyName}"? This will also delete all associated cases, clients, and invoices. This cannot be undone.`)) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/companies/${companyId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast({ title: 'Success', description: 'Company deleted' });
        fetchCompanies();
      } else {
        const err = await res.json();
        throw new Error(err.detail || 'Failed');
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const toggleStatus = async (companyId, currentStatus) => {
    const newStatus = currentStatus === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';
    try {
      const res = await fetch(`${BACKEND_URL}/api/companies/${companyId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        toast({ title: 'Success', description: `Company ${newStatus.toLowerCase()}` });
        fetchCompanies();
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const openEditDialog = (company) => {
    setSelectedCompany(company);
    setForm({
      name: company.name || '',
      type: company.type || 'investigation_agency',
      contact_email: company.contact_email || '',
      contact_phone: company.contact_phone || '',
      address: company.address || '',
      city: company.city || '',
      state: company.state || '',
      zip_code: company.zip_code || '',
      license_number: company.license_number || '',
      website: company.website || '',
      description: company.description || ''
    });
    setShowEdit(true);
  };

  const filtered = companies.filter(c => {
    if (!search) return true;
    const s = search.toLowerCase();
    return c.name?.toLowerCase().includes(s) || c.contact_email?.toLowerCase().includes(s) || c.city?.toLowerCase().includes(s);
  });

  return (
    <div className="space-y-6" data-testid="main-hub-companies">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Companies</h1>
          <p className="text-slate-400">Manage investigation companies on the platform</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-slate-300 text-slate-300" onClick={fetchCompanies}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button className="bg-[#0F172A] hover:bg-[#1E293B] text-white" data-testid="create-company-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Company
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-lg">
              <DialogHeader><DialogTitle>Add New Company</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Company Name *</Label>
                    <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                      className="bg-slate-50 border-slate-300 text-slate-900" placeholder="Acme Investigations" data-testid="company-name-input" />
                  </div>
                  <div>
                    <Label>Contact Email *</Label>
                    <Input value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })}
                      className="bg-slate-50 border-slate-300 text-slate-900" placeholder="contact@company.com" data-testid="company-email-input" />
                  </div>
                  <div>
                    <Label>Contact Phone</Label>
                    <Input value={form.contact_phone} onChange={e => setForm({ ...form, contact_phone: e.target.value })}
                      className="bg-slate-50 border-slate-300 text-slate-900" placeholder="(555) 000-0000" />
                  </div>
                  <div className="col-span-2">
                    <Label>Address</Label>
                    <Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })}
                      className="bg-slate-50 border-slate-300 text-slate-900" placeholder="Street address" />
                  </div>
                  <div>
                    <Label>City</Label>
                    <Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })}
                      className="bg-slate-50 border-slate-300 text-slate-900" />
                  </div>
                  <div>
                    <Label>State</Label>
                    <Input value={form.state} onChange={e => setForm({ ...form, state: e.target.value })}
                      className="bg-slate-50 border-slate-300 text-slate-900" />
                  </div>
                  <div>
                    <Label>Zip Code</Label>
                    <Input value={form.zip_code} onChange={e => setForm({ ...form, zip_code: e.target.value })}
                      className="bg-slate-50 border-slate-300 text-slate-900" placeholder="ZIP" />
                  </div>
                  <div>
                    <Label>License Number</Label>
                    <Input value={form.license_number} onChange={e => setForm({ ...form, license_number: e.target.value })}
                      className="bg-slate-50 border-slate-300 text-slate-900" placeholder="PI-12345" />
                  </div>
                  <div className="col-span-2">
                    <Label>Website</Label>
                    <Input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })}
                      className="bg-slate-50 border-slate-300 text-slate-900" placeholder="https://company.com" />
                  </div>
                  <div className="col-span-2">
                    <Label>Description</Label>
                    <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                      className="bg-slate-50 border-slate-300 text-slate-900" rows={2} placeholder="Brief company description..." />
                  </div>
                </div>
                <Button onClick={handleCreate} className="w-full bg-[#0F172A] hover:bg-[#1E293B] text-white" data-testid="submit-company-btn">
                  Create Company
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search */}
      <Card className="bg-white border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input value={search} onChange={e => setSearch(e.target.value)}
                className="bg-slate-50 border-slate-300 text-slate-900 pl-10" placeholder="Search companies..." data-testid="company-search" />
            </div>
            <div className="text-sm text-slate-400">
              {filtered.length} of {companies.length} companies
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Companies List */}
      {loading ? (
        <div className="text-center py-12 text-slate-400">Loading companies...</div>
      ) : filtered.length === 0 ? (
        <Card className="bg-white border-slate-200">
          <CardContent className="p-12 text-center">
            <Building2 className="w-16 h-16 mx-auto text-slate-600 mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Companies Found</h3>
            <p className="text-slate-400">{search ? 'Try a different search' : 'Add your first company to get started'}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.map(company => (
            <Card key={company.id} className="bg-white border-slate-200 hover:border-red-500/30 transition-colors" data-testid={`company-card-${company.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-red-500/10 rounded-lg flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-red-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">{company.name}</h3>
                      <p className="text-xs text-slate-500 font-mono">{company.id}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={`${company.status === 'ACTIVE' ? 'bg-green-500' : 'bg-red-500'} text-white text-xs`}>
                      {company.status}
                    </Badge>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div className="bg-slate-900/50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-slate-900">{company.user_count || 0}</p>
                    <p className="text-xs text-slate-400 flex items-center justify-center gap-1"><Users className="w-3 h-3" /> Users</p>
                  </div>
                  <div className="bg-slate-900/50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-slate-900">{company.case_count || 0}</p>
                    <p className="text-xs text-slate-400 flex items-center justify-center gap-1"><FolderOpen className="w-3 h-3" /> Cases</p>
                  </div>
                </div>

                <div className="space-y-1.5 text-sm mb-4">
                  <div className="flex items-center gap-2 text-slate-300">
                    <Mail className="w-4 h-4 text-slate-500" /> {company.contact_email}
                  </div>
                  {company.contact_phone && (
                    <div className="flex items-center gap-2 text-slate-300">
                      <Phone className="w-4 h-4 text-slate-500" /> {company.contact_phone}
                    </div>
                  )}
                  {(company.city || company.state) && (
                    <div className="flex items-center gap-2 text-slate-300">
                      <MapPin className="w-4 h-4 text-slate-500" /> {[company.address, company.city, company.state].filter(Boolean).join(', ')}
                    </div>
                  )}
                  {company.license_number && (
                    <div className="flex items-center gap-2 text-slate-300">
                      <Key className="w-4 h-4 text-slate-500" /> Lic: {company.license_number}
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-slate-700">
                  <p className="text-xs text-slate-600">Created: {new Date(company.created_at).toLocaleDateString()}</p>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline"
                      className={`text-xs ${company.status === 'ACTIVE' ? 'border-red-500/50 text-red-400 hover:bg-red-500/10' : 'border-green-500/50 text-green-400 hover:bg-green-500/10'}`}
                      onClick={() => toggleStatus(company.id, company.status)}
                      data-testid={`toggle-company-status-${company.id}`}>
                      {company.status === 'ACTIVE' ? 'Suspend' : 'Activate'}
                    </Button>
                    <button 
                      onClick={() => openEditDialog(company)} 
                      className="text-slate-400 hover:text-amber-400 p-1.5"
                      data-testid={`edit-company-${company.id}`}>
                      <Edit className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => handleDelete(company.id, company.name)} 
                      className="text-slate-500 hover:text-red-400 p-1.5"
                      data-testid={`delete-company-${company.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Company Dialog */}
      <Dialog open={showEdit} onOpenChange={(open) => { setShowEdit(open); if (!open) setSelectedCompany(null); }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-lg">
          <DialogHeader><DialogTitle>Edit Company</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Company Name *</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  className="bg-slate-50 border-slate-300 text-slate-900" placeholder="Acme Investigations" data-testid="edit-company-name-input" />
              </div>
              <div>
                <Label>Contact Email *</Label>
                <Input value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })}
                  className="bg-slate-50 border-slate-300 text-slate-900" placeholder="contact@company.com" data-testid="edit-company-email-input" />
              </div>
              <div>
                <Label>Contact Phone</Label>
                <Input value={form.contact_phone} onChange={e => setForm({ ...form, contact_phone: e.target.value })}
                  className="bg-slate-50 border-slate-300 text-slate-900" placeholder="(555) 000-0000" />
              </div>
              <div className="col-span-2">
                <Label>Address</Label>
                <Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })}
                  className="bg-slate-50 border-slate-300 text-slate-900" placeholder="Street address" />
              </div>
              <div>
                <Label>City</Label>
                <Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })}
                  className="bg-slate-50 border-slate-300 text-slate-900" />
              </div>
              <div>
                <Label>State</Label>
                <Input value={form.state} onChange={e => setForm({ ...form, state: e.target.value })}
                  className="bg-slate-50 border-slate-300 text-slate-900" />
              </div>
              <div>
                <Label>Zip Code</Label>
                <Input value={form.zip_code} onChange={e => setForm({ ...form, zip_code: e.target.value })}
                  className="bg-slate-50 border-slate-300 text-slate-900" placeholder="ZIP" />
              </div>
              <div>
                <Label>License Number</Label>
                <Input value={form.license_number} onChange={e => setForm({ ...form, license_number: e.target.value })}
                  className="bg-slate-50 border-slate-300 text-slate-900" placeholder="PI-12345" data-testid="company-license-input" />
              </div>
              <div className="col-span-2">
                <Label>Website</Label>
                <Input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })}
                  className="bg-slate-50 border-slate-300 text-slate-900" placeholder="https://company.com" data-testid="company-website-input" />
              </div>
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  className="bg-slate-50 border-slate-300 text-slate-900" rows={2} placeholder="Brief company description..." data-testid="company-description-input" />
              </div>
            </div>
            <Button onClick={handleEdit} className="w-full bg-[#0F172A] hover:bg-[#1E293B] text-white" data-testid="save-company-btn">
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MainHubCompanies;
